import * as pbm from '@/grpc-gen/reservation_pb';
import * as pbs from '@/grpc-gen/feedback_service_pb';

import ApiReservation, {
  ReservationMapping, ReservationSource, ReservationStatus, ReservationType,
} from '@/model/Reservation';
import { dateFromDateTimeString } from '@/services/time-utils';
import { ContactMapping } from '@/model/Contact';
import Label from '@/model/Label';
import { fixedPaymentInfo } from '@/services/billing-utils';
import Contact from './contact';
import Attachment from './attachment';

export default class Reservation extends ApiReservation {
  static fromGrpcResponse(r: pbs.FeedbackReservationResponse, m?: ReservationMapping): Reservation {
    return Reservation.fromGrpcModel(r.reservation, m);
  }

  static fromGrpcModel(o?: pbm.Reservation, m?: ReservationMapping): Reservation {
    const reservationTypeValues = ['', ReservationSource.Online, ReservationSource.User];
    const reservationStatusValues = ['',
      ReservationStatus.Planned,
      ReservationStatus.Seated,
      ReservationStatus.CheckedIn,
      ReservationStatus.CheckedOut,
      ReservationStatus.Confirmed,
      ReservationStatus.NonConfirmed,
      ReservationStatus.WaitingList,
      ReservationStatus.Cancelled,
      ReservationStatus.NoShow,
      ReservationStatus.Spam,
      ReservationStatus.Deleted,
      'pending',
    ];

    const r = new Reservation();

    r.id = Number(o?.id ?? 0);
    r.partySize = o?.partySize ?? 0;
    r.dateBegin = o?.dtBegin ? new Date(Number(o.dtBegin) * 1000) : new Date();
    r.dateEnd = o?.dtEnd ? new Date(Number(o.dtEnd) * 1000) : new Date();
    r.fixedEnding = o?.endFixed ?? false;
    r.holdTabItems = o?.holdTabItems ?? false;
    r.reservationType = reservationTypeValues[o?.reservationType ?? 0] as ReservationSource;
    r.code = o?.reservationCode ?? null;
    r.status = reservationStatusValues[o?.reservationStatus ?? 0] as ReservationStatus;
    r.isStarred = o?.isStarred ?? false;
    r.isFlagged = o?.isFlagged ?? false;
    r.isUnread = o?.isUnread ?? false;
    r.dateCreated = o ? new Date(Number(o.dtCreate) * 1000) : new Date();
    r.dateEdited = o ? new Date(Number(o.dtUpdate) * 1000) : new Date();

    r.tab = m?.tabsById.get(Number(o?.tabId ?? 0)) ?? null;
    r.service = m?.servicesById.get(Number(o?.serviceId ?? 0)) ?? null;
    r.employeeCreated = m?.employeesById.get(Number(o?.employeeIdCreate ?? 0)) ?? null;
    r.employeeEdited = m?.employeesById.get(Number(o?.employeeIdLastEdit ?? 0)) ?? null;
    r.reservationCampaign = o?.reservationCampaign ?? null;

    r.label = o?.reservationLabel
      ? m?.labelsByText.get(o?.reservationLabel) ?? new Label({ labelText: o?.reservationLabel })
      : null;

    const reservedTabItems = o?.tabItemsIds ?? [];
    r.tabItems = reservedTabItems.flatMap((ti) => m?.tabItemsById?.get(Number(ti ?? 0)) ?? []);
    r.tabItems.sort((o1, o2) => o1.order - o2.order);

    r.contact = Contact.fromGrpcModel(o?.contact, m as ContactMapping);

    r.notes = o?.noteText || null;
    r.paymentInfo = o?.paymentInfo ? fixedPaymentInfo(o.paymentInfo) : undefined;
    r.attachments = (o?.attachments ?? []).map((a) => Attachment.fromGrpcModel(a));

    r.rating = o?.rating ?? null;

    r.bookingType = ReservationType.Reservation;
    r.updateBookingType();
    r.updateSlotsAndTimes();

    return r;
  }
}
